// Signed-out site header: logo, Product menu, Enterprise / Pricing / News / Changelog, and Log in + Sign up
// (or Open Workspace when already signed in). Below 860px the links move into a drop-down panel.
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faBars, faChevronDown, faXmark } from '@fortawesome/free-solid-svg-icons';
import { AuthContext } from '../../AuthContext';
import { PRODUCTS } from './siteLinks';
import bumpups_logo from '../../assets/images/default/bumpups-logo-final.png';
import './siteNav.css';

const PAGES = [
  { to: '/use-cases', label: 'Use cases' },
  { to: '/tools', label: 'Free tools' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/enterprise', label: 'Enterprise' },
];

const navClass = ({ isActive }) => `mkn-link${isActive ? ' active' : ''}`;

export default function SiteNav() {
  const { currentUser } = useContext(AuthContext);
  const { pathname } = useLocation();
  const [productOpen, setProductOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const productRef = useRef(null);

  // Any navigation closes whatever is open
  useEffect(() => {
    setProductOpen(false);
    setPanelOpen(false);
  }, [pathname]);

  // The Product menu closes on an outside click or Escape
  useEffect(() => {
    if (!productOpen && !panelOpen) return undefined;
    const onDown = e => {
      if (productOpen && productRef.current && !productRef.current.contains(e.target)) setProductOpen(false);
    };
    const onKey = e => {
      if (e.key === 'Escape') { setProductOpen(false); setPanelOpen(false); }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [productOpen, panelOpen]);

  const onProductPage = PRODUCTS.some(p => pathname === p.to || pathname.startsWith(`${p.to}/`));

  const accountActions = currentUser ? (
    <Link to="/workspace" className="mk-btn mk-btn--primary mk-btn--sm">
      Open Workspace <FontAwesomeIcon icon={faArrowRight} className="mk-btn-arrow" />
    </Link>
  ) : (
    <>
      <Link to="/signin" className="mkn-login">Log in</Link>
      <Link to="/signup" className="mk-btn mk-btn--primary mk-btn--sm">Sign up</Link>
    </>
  );

  return (
    <header className={`mkn${panelOpen ? ' is-open' : ''}`}>
      <div className="mk-container mkn-bar">
        <Link to="/" className="mkn-logo" aria-label="Bumpups home">
          <img src={bumpups_logo} alt="Bumpups" width="107" height="32" />
        </Link>

        <nav className="mkn-links" aria-label="Main">
          <div className="mkn-product" ref={productRef}>
            <button
              type="button"
              className={`mkn-link mkn-product-toggle${onProductPage ? ' active' : ''}`}
              aria-expanded={productOpen}
              aria-controls="mkn-product-menu"
              onClick={() => setProductOpen(open => !open)}
            >
              Product <FontAwesomeIcon icon={faChevronDown} className="mkn-chevron" />
            </button>
            <div id="mkn-product-menu" className={`mkn-menu${productOpen ? ' is-open' : ''}`}>
              {PRODUCTS.map(p => (
                <Link key={p.to} to={p.to} className="mkn-menu-item">
                  <span className="mk-icon"><FontAwesomeIcon icon={p.icon} /></span>
                  <span>
                    <span className="mkn-menu-title">{p.title}</span>
                    <span className="mkn-menu-blurb">{p.blurb}</span>
                  </span>
                </Link>
              ))}
            </div>
          </div>
          {PAGES.map(p => <NavLink key={p.to} to={p.to} className={navClass}>{p.label}</NavLink>)}
        </nav>

        <div className="mkn-right">
          <div className="mkn-account">{accountActions}</div>
          <button
            type="button"
            className="mkn-burger"
            aria-label={panelOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={panelOpen}
            aria-controls="mkn-panel"
            onClick={() => setPanelOpen(open => !open)}
          >
            <FontAwesomeIcon icon={panelOpen ? faXmark : faBars} />
          </button>
        </div>
      </div>

      {panelOpen && (
        <div id="mkn-panel" className="mkn-panel">
          <div className="mk-container">
            <p className="mkn-panel-heading">Product</p>
            {PRODUCTS.map(p => (
              <Link key={p.to} to={p.to} className="mkn-panel-product">
                <span className="mk-icon"><FontAwesomeIcon icon={p.icon} /></span>
                <span>
                  <span className="mkn-menu-title">{p.title}</span>
                  <span className="mkn-menu-blurb">{p.blurb}</span>
                </span>
              </Link>
            ))}
            <div className="mkn-panel-links">
              {PAGES.map(p => <NavLink key={p.to} to={p.to} className={navClass}>{p.label}</NavLink>)}
            </div>
            <div className="mkn-panel-account">{accountActions}</div>
          </div>
        </div>
      )}
    </header>
  );
}
